import React from "react";
import { useTranslation } from "react-i18next";
import {
  AVATAR_SECTION_PATH, castableAvatars, createBlockOf, faceLockCostLabel, isTraining, isUnlocked, type FaceLockAvatar, type FaceLockCrew,
} from "@/lib/facelock";
import FaceLockPicker, { type FaceLockPickerPalette } from "./FaceLockPicker";
import NewFaceLockDialog from "./NewFaceLockDialog";
import CreateAvatarNeedsDialog from "./CreateAvatarNeedsDialog";
import { faceLockScanSlot } from "./scan/FaceLockScan";

// The "who stars in it" step when a FaceLock is REQUIRED (FACELOCK_CREW_REQUIRED,
// owner rule 2026-09-30): a music video with the artist in it, or an ad with a
// person in it, is made with a FaceLock or not at all. This REPLACES the photo
// uploader in both hosts (the corrido artist step, the ads "Put me in the ad"):
//
//   * pick one of your ready FaceLocks (no "New photos" option);
//   * FaceLocks still training show their progress; the one this build waits
//     for says the build carries on by itself once it is ready;
//   * a legacy FaceLock with no photos on file says so and is not offered;
//   * "Create your avatar" opens the create dialog (the owner's photo guidance,
//     one set of 16 to 24 photos, a name) right here;
//   * not unlocked yet: exactly what unlocks it (sign up, buy credits: the
//     first FaceLock is free once enough credits were bought), and the way out
//     without a face.
//   * the Create button is there in EVERY state and always pressable
//     (2026-10-02): when it can't open the create dialog (gate, an avatar
//     training, closed) it opens CreateAvatarNeedsDialog, which says why.
//
// Inline styles on purpose, like FaceLockPicker: both hosts are inline-styled
// chat cards with their own palettes.

type Props = {
  crew: FaceLockCrew;
  /** The chosen FaceLock's id (only a castable one is ever chosen). */
  value: string | null;
  onChange: (avatarId: string | null) => void;
  /** The FaceLock this build is waiting on (made here, still training). */
  waitingId: string | null;
  /** A FaceLock was just made in the dialog: the host saves its choices and waits. */
  onCreated: (avatar: FaceLockAvatar | null) => void;
  /** The way out without a face (corrido: faceless mode; ads: no person). */
  onNoFace?: () => void;
  noFaceLabel?: string;
  /** Why a FaceLock, in the host's words. */
  intro: string;
  /** What happens once the awaited FaceLock is ready, in the host's words ({{name}}). */
  waitingNote: (name: string) => string;
  palette: FaceLockPickerPalette;
  testIdPrefix: string;
  disabled?: boolean;
  /** What the picker offers; default the castable ones. A corrido storyboard passes
   *  boardAvatars() (lib/facelock-stills.ts): also avatars whose video files still train. */
  pickable?: FaceLockAvatar[];
};

export default function FaceLockCastStep({
  crew, value, onChange, waitingId, onCreated, onNoFace, noFaceLabel, intro, waitingNote, palette, testIdPrefix, disabled, pickable,
}: Props) {
  const { t, i18n } = useTranslation();
  const [creating, setCreating] = React.useState(false);
  const [needsOpen, setNeedsOpen] = React.useState(false);
  const e = crew.eligibility;
  const castable = pickable ?? castableAvatars(crew);
  const training = crew.avatars.filter((a) => !a.castable && isTraining(a) && !castable.some((p) => p.id === a.id));
  const needsPhotos = crew.avatars.filter((a) => a.needs_photos && !a.castable);
  const awaited = waitingId ? crew.avatars.find((a) => a.id === waitingId) || null : null;
  const awaitedFailed = awaited && awaited.status === "failed" ? awaited : null;
  const unlocked = isUnlocked(e);
  const bought = e.purchased_credits >= e.required_purchased || e.gate_waived === true;
  const block = createBlockOf(e, crew.avatars);
  const messageOf = (a: FaceLockAvatar) => {
    const key = a.message_key ? `faceLock.messages.${a.message_key}` : "";
    return key && i18n.exists(key) ? t(key) : "";
  };
  const line: React.CSSProperties = { fontSize: 12, color: palette.muted, lineHeight: 1.5 };
  const box: React.CSSProperties = {
    borderRadius: 9, border: `1px solid ${palette.border}`, background: palette.surface, padding: "8px 10px", display: "grid", gap: 4,
  };
  const button = (primary: boolean): React.CSSProperties => ({
    justifySelf: "start", borderRadius: 9, cursor: disabled ? "default" : "pointer", fontSize: 12, fontWeight: 700, padding: "8px 14px",
    border: primary ? "none" : `1px solid ${palette.border}`, background: primary ? palette.accent : "transparent",
    color: primary ? "#05070b" : palette.text, opacity: disabled ? 0.6 : 1,
  });
  const link: React.CSSProperties = { color: palette.accent, fontWeight: 700, textDecoration: "underline" };

  return (
    <div data-testid={testIdPrefix} style={{ display: "grid", gap: 8, marginBottom: 8 }}>
      <div style={{ fontWeight: 700, color: palette.white, fontSize: 12 }}>{t("faceLock.required.title")}</div>
      <div style={line}>{intro}</div>

      {castable.length > 0 && (
        <FaceLockPicker
          avatars={castable}
          value={value}
          onChange={onChange}
          palette={palette}
          testIdPrefix={`${testIdPrefix}-picker`}
          disabled={disabled}
          allowNewPhotos={false}
          hint={t("faceLock.required.pickHint")}
        />
      )}

      {training.map((a) => (
        <div key={a.id} data-testid={`${testIdPrefix}-training-${a.id}`} style={box}>
          <div style={{ fontSize: 12, fontWeight: 700, color: palette.text }}>
            {a.progress !== null && a.progress > 0
              ? t("faceLock.required.training", { name: a.name, progress: Math.round(a.progress) })
              : t("faceLock.required.trainingStarting", { name: a.name })}
          </div>
          <div style={{ height: 6, borderRadius: 3, background: palette.border, overflow: "hidden" }}>
            <div
              data-testid={`${testIdPrefix}-progress-${a.id}`}
              style={{ height: "100%", width: `${Math.max(2, Math.min(100, a.progress ?? 0))}%`, background: palette.accent }}
            />
          </div>
          {a.id === waitingId && (
            <div data-testid={`${testIdPrefix}-waiting`} style={{ ...line, color: palette.text }}>{waitingNote(a.name)}</div>
          )}
        </div>
      ))}

      {awaitedFailed && (
        <div data-testid={`${testIdPrefix}-failed`} role="alert" style={{ ...box, borderColor: "#f87171" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: palette.text }}>{t("faceLock.required.failed", { name: awaitedFailed.name })}</div>
          {messageOf(awaitedFailed) && <div style={line}>{messageOf(awaitedFailed)}</div>}
        </div>
      )}

      {needsPhotos.map((a) => (
        <div key={a.id} data-testid={`${testIdPrefix}-needs-photos-${a.id}`} style={line}>
          <b style={{ color: palette.text }}>{a.name}:</b> {t("faceLock.messages.needs_photos")}
        </div>
      ))}

      {!unlocked && (
        <div data-testid={`${testIdPrefix}-gate`} style={box}>
          <div style={{ fontSize: 12, fontWeight: 700, color: palette.white }}>{t("faceLock.required.gateTitle")}</div>
          {!e.signed_up && (
            <div data-testid={`${testIdPrefix}-gate-signup`} style={line}>
              {t("faceLock.required.signUp")}{" "}
              <a href={AVATAR_SECTION_PATH} style={link}>{t("faceLock.required.openAccount")}</a>
            </div>
          )}
          {!bought && (
            <div data-testid={`${testIdPrefix}-gate-credits`} style={line}>
              {t("faceLock.required.firstFree", { required: e.required_purchased })}{" "}
              {t("faceLock.gate.bought", { n: e.purchased_credits, required: e.required_purchased })}.{" "}
              <a href="/pricing" data-testid={`${testIdPrefix}-gate-buy`} style={link}>{t("faceLock.gate.buyCredits")}</a>
            </div>
          )}
        </div>
      )}
      {unlocked && block && (
        <div data-testid={`${testIdPrefix}-create-blocked`} style={line}>
          {block === "busy" ? t("faceLock.gate.busy") : t("faceLock.gate.closed")}
        </div>
      )}
      {/* ALWAYS here and pressable (2026-10-02): unlocked and free to create,
          the create dialog right here; otherwise exactly what is missing. */}
      <button
        type="button"
        data-testid={`${testIdPrefix}-create`}
        data-block={block || ""}
        disabled={disabled}
        onClick={() => (block ? setNeedsOpen(true) : setCreating(true))}
        style={button(castable.length === 0)}
      >
        {!unlocked
          ? t("faceLock.required.createPlain")
          : t(castable.length ? "faceLock.required.createAnother" : "faceLock.required.create", { cost: faceLockCostLabel(t, e.next_cost) })}
      </button>

      {onNoFace && castable.length === 0 && (
        <button type="button" data-testid={`${testIdPrefix}-no-face`} disabled={disabled} onClick={onNoFace} style={button(false)}>
          {noFaceLabel}
        </button>
      )}

      <NewFaceLockDialog
        open={creating}
        onOpenChange={setCreating}
        eligibility={e}
        onCreated={(a) => { onCreated(a); }}
        scanSlot={faceLockScanSlot}
      />
      <CreateAvatarNeedsDialog open={needsOpen} onOpenChange={setNeedsOpen} block={block} eligibility={e} />
    </div>
  );
}
